import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import useCreateIndexador from "core/features/indexador/hooks/useCreateIndexador";
import useEditIndexador from "core/features/indexador/hooks/useEditIndexador.ts";
import useGetIndexador from "core/features/indexador/hooks/useGetIndexador";
import { IndexadorForm } from "core/features/indexador/typings";
import useAllUnidades from "core/features/unidades/useAllUnidades";
import { showConfirm } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import IndexadorTable from "ui/components/IndexadorTable";
import FormIndexadorHeader from "./FormIndexadorHeader";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { formatMoney } from "infra/helpers/number";

function FormIndexador({
  valorUnitario,
  onSuccess,
  onClose,
  visible,
  indexadorEditId,
}: {
  valorUnitario: number;
  onSuccess?: (data: any) => Promise<void>;
  onClose?: () => void;
  visible: boolean;
  indexadorEditId?: string;
}) {
  const { tenant } = useCurrentTenant();
  const {
    data: indexadorEditData,
    isLoading: isLoadingIndexador,
    isFetching: isFetchingIndexador,
  } = useGetIndexador(indexadorEditId);
  const [search, setSearch] = useState("");

  const {
    data: allUnidades,
    isLoading: isLoadingAllUnidades,
    isFetching: isFetchingAllUnidades,
  } = useAllUnidades(tenant.id, {
    limit: 99999,
  });

  const [filteredUnidades, setFilteredUnidades] = useState(allUnidades);

  const [tableKey, setTableKey] = useState(+new Date());
  const [enabledEdit, setEnabledEdit] = useState(false);
  const { control, handleSubmit, reset, formState, watch } =
    useForm<IndexadorForm>({
      reValidateMode: "onChange",
      mode: "onChange",
    });

  const form = watch();

  const { isLoading: isLoadingCreate, mutate: handleCreate } =
    useCreateIndexador();
  const { isLoading: isLoadingEdit, mutate: handleEdit } = useEditIndexador();

  useEffect(() => {
    if (indexadorEditId && indexadorEditData && allUnidades?.items) {
      reset({
        nome: indexadorEditData.nome,
        unidades: indexadorEditData.unidades.reduce((acc, next) => {
          acc[next.id] = next.valor;
          return acc;
        }, {} as Record<string, any>),
      });
      setFilteredUnidades(allUnidades);
      setTableKey(+new Date());
    } else if (allUnidades?.items && !isLoadingIndexador) {
      reset({
        nome: "Indexador genérico",
      });
      setTableKey(+new Date());
      setFilteredUnidades(allUnidades);
    }
  }, [
    allUnidades,
    indexadorEditData,
    indexadorEditId,
    isLoadingIndexador,
    reset,
  ]);

  const onSubmit = async (data: IndexadorForm) => {
    const mappedData = {
      nome: data.nome,
      unidades: Object.keys(data.unidades).map((key) => ({
        id: key,
        valor: data.unidades[key],
      })),
    };
    if (indexadorEditData?.id) {
      showConfirm(
        "Deseja salvar o novo peso ideal? Obs: Caso exista peso ideal cadastrado em alguma unidade ele será substituido"
      ).then(({ isConfirmed }) => {
        isConfirmed &&
          handleEdit(
            { id: indexadorEditData.id, data: mappedData },
            { onSuccess: () => onSuccess?.(indexadorEditData.id) }
          );
      });
    } else {
      handleCreate(
        { data: mappedData },
        { onSuccess: ({ data }) => onSuccess?.(data?.id) }
      );
    }
  };

  const onSearch = (q: string | undefined) => {
    if (!allUnidades) {
      return;
    }

    if (!q) {
      setFilteredUnidades(allUnidades);
      return;
    }

    const filteredItems = allUnidades?.items.filter((u) => {
      let nomeUnidade = `Número ${u.numero}`;

      if (u.blocoQuadra) {
        nomeUnidade += ` - Bloco/Quadra ${u.blocoQuadra.nome}`;
      }

      const nomeProprietario = u.pessoa?.nome;

      return (
        nomeUnidade.toLowerCase().includes(q.toLowerCase()) ||
        nomeProprietario?.toLowerCase().includes(q.toLowerCase())
      );
    });
    setFilteredUnidades({ ...allUnidades, items: filteredItems });
    setSearch(q);
  };

  const _isLoadingData =
    isLoadingIndexador ||
    isLoadingAllUnidades ||
    isFetchingIndexador ||
    isFetchingAllUnidades;

  const isLoadingMutate = isLoadingCreate || isLoadingEdit;

  return (
    <form
      onSubmit={(ev) => handleSubmit(onSubmit)(ev)}
      style={{ display: "flex", flex: 1, height: "100%" }}
    >
      <Flex flexDir="column" flex={1} h="100%" flexGrow={1}>
        <Flex mb={4} h="40px">
          <FormIndexadorHeader
            onEnableEdit={() => setEnabledEdit((state) => !state)}
            onExcelInput={() => null}
            enabledEdit={enabledEdit}
            control={control}
            loading={_isLoadingData}
          />
        </Flex>

        <Box d="flex" mt={4} gridGap="8px" flexDir="row" pb={"16px"} px={2}>
          <Text mr="auto" fontSize="20px" fontWeight="500">
            Valor unitário: {formatMoney(valorUnitario)}
          </Text>
          <OutlineButton
            onClick={() => {
              onClose?.();
              reset();
            }}
          >
            Cancelar
          </OutlineButton>

          <PrimaryButton
            type="button"
            isLoading={isLoadingMutate}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || isLoadingMutate}
            minWidth={"120px"}
          >
            Salvar
          </PrimaryButton>
        </Box>
        <Box overflowY="auto" pb={16} pt={4} px={2}>
          <IndexadorTable
            valor={valorUnitario}
            form={form}
            key={tableKey}
            control={control}
            data={filteredUnidades}
            searchValue={search}
            onSearch={onSearch}
            enabledEdit={enabledEdit}
            isLoading={_isLoadingData}
          />
        </Box>
      </Flex>
    </form>
  );
}

export default FormIndexador;
